import { call, put, takeLeading, select, fork } from "redux-saga/effects";
import { DisclaimerType, FATCA, MuleAccountPopupError, RiskProfileErrors, TabName } from "../../const";
import { push, replace } from "connected-react-router";
import { LandingPage, Routes } from "../../const/routes";
import { DisclaimerAPI } from "../../services/disclaimer";
import { CustomerAPI } from "../../services/customer";
import { isThai, toUrlSearchParam } from "../../utils/common-util";
import { DisclaimerSelector, MasterSelector } from "redux/selector";
import { flagSsoError, setAuthenticating, signOut } from "redux/reducers/auth";
import { alertError } from "redux/reducers/master";
import { closeModal, openModal } from "redux/reducers/central-modal";
import {
  accept,
  acceptDisclaimer,
  acceptPDPA,
  getDisclaimerList,
  getDisclaimerListFailure,
  getDisclaimerListSuccess,
  notAccept,
  acceptDisclaimerSuccess,
  acceptDisclaimerFailure,
  acceptSuccess,
  acceptFailure,
  validInvestorProperties,
  acceptPDPASuccess,
  acceptPDPAFailure,
  redirectToPrimaryProduct,
  redirectToSecondaryProduct,
  redirectToRiskResult,
  redirectToBuySellSwitch,
  redirectToModelPortfolio,
} from "redux/reducers/disclaimer";
import { showSuitabilityRequiredModal } from "redux/reducers/modals/suitability";

import {
  checkRegistered,
  checkRegisteredFailure,
  checkRegisteredSuccess,
  maskAsMuleAccount,
} from "redux/reducers/customer";
import { setRedirectUrl } from "redux/reducers/redirect";
import { handleClientError } from "utils/saga-error";
import { clearLoadingIndex } from "redux/reducers/ultrahigh-home";
import { CustomerSelector } from "redux/selector/customer-selector";
import { checkRiskProfile, checkUserTypeSaga, isPassedTheBankruptcyQualificationCheck } from "./customer";
import { StepControl } from "utils/misc/step-control";
import store from "redux/store";
import { RiskProfileError } from "classes/error/RiskProfileError";
import { getPrimaryProductDetail } from "redux/reducers/product";
import { getProductOfferingDetail, onTabChange } from "redux/reducers/second-market";
import { oAuthStorage } from "utils/o-auth-utils";

function* acceptAndValidateSaga({ payload }) {
  const { skipLandingPage, isSingleSignOn } = payload ?? {};
  try {
    yield put(setAuthenticating(true));
    const { data: cbsDataIncomplete, age: ageNotAllow, ...rest } = yield call(checkAgeAndDataIncomplete); // CBS Data Incomplete And Age Under 20

    const errorCode = cbsDataIncomplete ?? ageNotAllow;
    if (!errorCode) {
      const alreadyAccept = yield call(checkAcceptPDPA); // Check Accept PDPA
      if (!alreadyAccept) {
        yield put(openModal({ code: DisclaimerType.PDPA })); // Should Accept PDPA First
        yield put(acceptFailure());
      } else {
        let [fatcaAmlValid, code] = yield call(checkFatcaAml); // Continue Check Fatca/AML
        // let [fatcaAmlValid, code] = [false, "FATCA_NO_DATA"]; // Continue Check Fatca/AML

        // FATCA 2 Question Popup
        if (!fatcaAmlValid && code === FATCA.NO_DATA) {
          const [answerPayload] = yield new StepControl([FATCA.NO_DATA], (code, [next, _, cancel]) => {
            const actions = {
              onAccept: next,
              onCancel: () => cancel({ message: "cancel fatca question", ignore: true }),
            };
            store.dispatch(openModal({ code, actions }));
          }).start();

          //* if not pass update api will throw error 73001
          yield call(DisclaimerAPI.saveFatcaAml, answerPayload);
          fatcaAmlValid = true;
        }
        // [End] FATCA 2 Question Popup

        if (fatcaAmlValid) {
          const { acceptDisc, acceptDiscVersion } = yield select(CustomerSelector.userInfo);
          if (acceptDisc) {
            yield fork(acceptDisclaimerProcess, {
              code: DisclaimerType.DisclaimerPage,
              acceptDiscVersion: acceptDiscVersion,
            });
          }
          yield put(acceptSuccess());

          if (skipLandingPage) {
            // skipLandingPage will be [true] in case of oauth with 'program' param
            yield put(validInvestorProperties());
          } else {
            //* Deep Link
            const deepLinkInfo = oAuthStorage.deepLinkInfo;
            // properly landing page
            const [pageRoute, canAccessInnerPage] = yield call(selectLandingPage, { payload: { deepLinkInfo } });
            // allow to access inner page
            // if not then user need to goto risk profile or register page first
            if (canAccessInnerPage) {
              yield put(validInvestorProperties());
            }
            // redirect to target page
            if (pageRoute === Routes.Primary.Products) {
              yield call(redirectToPrimaryProductSaga, {
                payload: { replaceUrl: isSingleSignOn, throwError: true, deepLinkInfo },
              });
            } else if (pageRoute === Routes.Secondary.Products) {
              yield call(redirectToSecondaryProductSaga, {
                payload: { replaceUrl: isSingleSignOn, throwError: true, deepLinkInfo },
              });
            } else if (
              pageRoute.startsWith(Routes.UltraHigh.BuySellSwitching.Buy) ||
              pageRoute.startsWith(Routes.UltraHigh.BuySellSwitching.Sell) ||
              pageRoute.startsWith(Routes.UltraHigh.BuySellSwitching.Switch)
            ) {
              yield call(redirectToBuySellSwitchSaga, {
                payload: { pageRoute, replaceUrl: isSingleSignOn, throwError: true, deepLinkInfo },
              });
            } else {
              //* User need to go somewhere else before rediect to required page
              const userNotRegisterYet = pageRoute === Routes.Register.CustomerInfo;
              const userNeedToDoSuitTest = pageRoute === Routes.Register.Suitability;

              if (userNotRegisterYet) {
                const isMuleAccount = yield select(CustomerSelector.isMuleAccount);
                if (isMuleAccount) {
                  yield put(alertError(MuleAccountPopupError));
                  yield put(replace(Routes.SignIn));
                  return;
                }
              }

              if (userNotRegisterYet || userNeedToDoSuitTest) {
                yield put(
                  setRedirectUrl({
                    id: "suitability",
                    dispatchAction: redirectToPrimaryProduct({ replaceUrl: isSingleSignOn, throwError: true }),
                  }),
                );
              }

              const redirect = isSingleSignOn ? replace : push;
              yield put(redirect(pageRoute));
            }
          }
        } else {
          yield call(investorPropertiesInvalid, { code });
        }
      }
    } else {
      yield put(acceptFailure());
      yield call(investorPropertiesInvalid, { code: errorCode, ...rest });
    }
  } catch (error) {
    if (isSingleSignOn) {
      yield put(flagSsoError());
    }

    if (error instanceof RiskProfileError) {
      yield put(replace(Routes.SystemIsNotReady));
    } else {
      yield put(replace(Routes.SignIn));
    }

    yield put(acceptFailure(handleClientError(error)));
  } finally {
    yield put(setAuthenticating(false));
  }
}

/**
 * select landing page between portfolio or primary market page if user can
 * condition : if user is pwm
 * return value : like function canUserNavigateTo() below
 */
function* selectLandingPage({ payload: { deepLinkInfo } }) {
  yield call(checkUserTypeSaga);
  const isPwmUser = yield select(CustomerSelector.isPwmUser);

  // isPwmUser flag denote PB, PPB, PWM user
  if (isPwmUser) {
    // case pwm user: no need to check register and risk profile status
    return [deepLinkInfo?.requestURL ?? LandingPage.PWM, true];
  }

  // check register and risk profile status
  // case: ultrahigh and retail user
  return yield call(canUserNavigateTo, deepLinkInfo?.requestURL ?? LandingPage.Retail);
}

/**
 * check register status and validate risk profile (it'll flag new result of checkRiskProfile to store)
 * return value:
 *  - index 0 : route path that user should redirect to or targetPathname
 *  - index 1 : specifies that you can navigate to targetPathname like you want or not.
 *              >> pass register and risk profile valid or not <<
 */
function* canUserNavigateTo(targetPathname, options) {
  const { checkRegister: shouldCheckRegister = true, checkRiskProfile: _checkRiskProfile = true } = options ?? {};

  if (shouldCheckRegister) {
    let alreadyRegister = yield select(CustomerSelector.registered);
    if (alreadyRegister === null) {
      //* fetch the register status first
      alreadyRegister = yield call(checkRegister);
    }
    if (!alreadyRegister) {
      yield put(setRedirectUrl({ id: "suitability", url: targetPathname, replace: true }));
      return [Routes.Register.CustomerInfo, false, { invalidRegisterState: true }];
    }
  }

  if (_checkRiskProfile) {
    const validSuitability = yield call(checkRiskProfile);
    if (!validSuitability) {
      yield put(setRedirectUrl({ id: "suitability", url: targetPathname, replace: true }));
      return [Routes.Register.Suitability, false, { invalidSuitabilityState: true }];
    }
  }

  return [targetPathname, true, {}];
}

function* redirectToPrimaryProductSaga({ payload }) {
  const { replaceUrl, clearLoadingStatus, throwError, redirectToProduct } = payload ?? {};
  try {
    const redirectMethod = replaceUrl ? replace : push;
    const targetPage = Routes.Primary.Products;
    const [pageRoute, accessible, cause] = yield call(canUserNavigateTo, targetPage);
    if (accessible) {
      if (yield call(isPassedTheBankruptcyQualificationCheck)) {
        if (redirectToProduct) {
          yield put(getPrimaryProductDetail(redirectToProduct));
        } else {
          yield put(redirectMethod(targetPage));
        }
      }
    } else if (cause.invalidRegisterState) {
      const isMuleAccount = yield select(CustomerSelector.isMuleAccount);
      if (isMuleAccount) {
        yield put(alertError(MuleAccountPopupError));
        return;
      }

      yield put(
        setRedirectUrl({
          id: "suitability",
          dispatchAction: redirectToPrimaryProduct(payload),
        }),
      );

      yield put(redirectMethod(pageRoute));
    } else if (cause.invalidSuitabilityState) {
      yield put(
        showSuitabilityRequiredModal({
          redirectConfig: {
            dispatchAction: redirectToPrimaryProduct(payload),
          },
        }),
      );
    }
  } catch (error) {
    if (error instanceof RiskProfileError) {
      yield put(openModal({ code: RiskProfileErrors.SystemNotReady }));
    } else {
      yield put(alertError(handleClientError(error)));
    }
    if (throwError) throw error;
  } finally {
    if (clearLoadingStatus) {
      yield put(clearLoadingIndex());
    }
  }
}

// function* redirectToSecondaryProductDetail(securityCode) {
//   try {
//     const response = yield call(SubscriptionAPI.check2ndProductReadiness, { securityCode });
//     if (response.data.result?.active) {
//       yield put(getProductOfferingDetail({ securityCode }));
//     }
//   } catch (error) {
//     console.error(error);
//   }
// }

function* redirectToSecondaryProductSaga({ payload }) {
  const { replaceUrl, clearLoadingStatus, throwError, deepLinkInfo } = payload ?? {};
  const securityCode = deepLinkInfo?.securityCode;
  try {
    const redirectMethod = replaceUrl ? replace : push;
    const targetPage = Routes.Secondary.Products;
    const [pageRoute, accessible, cause] = yield call(canUserNavigateTo, Routes.Secondary.Products);
    if (accessible) {
      if (yield call(isPassedTheBankruptcyQualificationCheck)) {
        const activeTabKey = deepLinkInfo?.activeTabKey ?? TabName.SECOND_BUY_SELL;
        yield put(onTabChange(activeTabKey));
        yield put(redirectMethod(targetPage));
        if (deepLinkInfo) {
          oAuthStorage.deepLinkInfo = null;
        }
        if (securityCode) {
          yield put(getProductOfferingDetail({ securityCode }));
        }
      }
    } else if (cause.invalidRegisterState) {
      const isMuleAccount = yield select(CustomerSelector.isMuleAccount);
      if (isMuleAccount) {
        yield put(alertError(MuleAccountPopupError));
        return;
      }

      yield put(
        setRedirectUrl({
          id: "suitability",
          dispatchAction: redirectToSecondaryProduct(payload),
        }),
      );
      yield put(redirectMethod(pageRoute));
    } else if (cause.invalidSuitabilityState) {
      yield put(
        showSuitabilityRequiredModal({
          redirectConfig: {
            dispatchAction: redirectToSecondaryProduct(payload),
          },
        }),
      );
    }
  } catch (error) {
    if (error instanceof RiskProfileError) {
      yield put(openModal({ code: RiskProfileErrors.SystemNotReady }));
    } else {
      yield put(alertError(handleClientError(error)));
    }
    if (throwError) throw error;
  } finally {
    if (clearLoadingStatus) {
      yield put(clearLoadingIndex());
    }
  }
}

function* redirectToRiskResultSaga({ payload }) {
  const { replaceUrl, clearLoadingStatus } = payload ?? {};
  try {
    const targetPage = Routes.Profile;
    const [pageRoute] = yield call(canUserNavigateTo, Routes.Register.RiskResult, { checkRegister: false });
    const redirectMethod = replaceUrl ? replace : push;
    yield put(setRedirectUrl({ id: "suitability", url: targetPage }));
    yield put(redirectMethod(pageRoute));
  } catch (error) {
    if (error instanceof RiskProfileError) {
      yield put(openModal({ code: RiskProfileErrors.SystemNotReady }));
    } else {
      yield put(alertError(handleClientError(error)));
    }
  } finally {
    if (clearLoadingStatus) {
      yield put(clearLoadingIndex());
    }
  }
}

function* redirectToBuySellSwitchSaga({ payload }) {
  const { type, pageRoute, queryParams, replaceUrl, clearLoadingStatus, throwError } = payload ?? {};
  try {
    const redirectMethod = replaceUrl ? replace : push;
    const pathname =
      pageRoute ??
      {
        buy: Routes.UltraHigh.BuySellSwitching.Buy,
        sell: Routes.UltraHigh.BuySellSwitching.Sell,
        switch: Routes.UltraHigh.BuySellSwitching.Switch,
      }[type.toLowerCase()];
    const searchParams = toUrlSearchParam(queryParams);
    const targetPage = pathname + searchParams;
    const [, accessible] = yield call(canUserNavigateTo, targetPage, { checkRegister: false });
    if (accessible) {
      if (yield call(isPassedTheBankruptcyQualificationCheck)) {
        yield put(redirectMethod(targetPage));
      }
    } else {
      yield put(
        showSuitabilityRequiredModal({
          redirectConfig: {
            dispatchAction: redirectToBuySellSwitch(payload),
          },
        }),
      );
    }
  } catch (error) {
    if (error instanceof RiskProfileError) {
      yield put(openModal({ code: RiskProfileErrors.SystemNotReady }));
    } else {
      yield put(alertError(handleClientError(error)));
    }
    if (throwError) throw error;
  } finally {
    if (clearLoadingStatus) {
      yield put(clearLoadingIndex());
    }
  }
}

function* redirectToModelPortfolioSaga({ payload }) {
  const { replaceUrl, clearLoadingStatus } = payload ?? {};
  try {
    const redirectMethod = replaceUrl ? replace : push;
    const targetPage = Routes.UltraHigh.ModelPortfolio;
    const [, accessible, cause] = yield call(canUserNavigateTo, Routes.UltraHigh.ModelPortfolio, {
      checkRegister: false,
    });
    if (accessible) {
      yield put(redirectMethod(targetPage));
    } else if (cause.invalidSuitabilityState) {
      yield put(
        showSuitabilityRequiredModal({
          redirectConfig: {
            url: targetPage,
            replace: true,
          },
        }),
      );
    }
  } catch (error) {
    if (error instanceof RiskProfileError) {
      yield put(openModal({ code: RiskProfileErrors.SystemNotReady }));
    } else {
      yield put(alertError(handleClientError(error)));
    }
  } finally {
    if (clearLoadingStatus) {
      yield put(clearLoadingIndex());
    }
  }
}

export function* checkRegister() {
  try {
    const response = yield call(CustomerAPI.isRegistered);
    yield put(checkRegisteredSuccess(response.data?.result?.registered));
    return response.data?.result?.registered;
  } catch (error) {
    yield put(checkRegisteredFailure(error));
  }
}

function* acceptPDPASaga() {
  try {
    const payload = { code: DisclaimerType.PDPA };
    yield call(acceptDisclaimerProcess, payload);
    yield put(accept());
    yield put(closeModal());
    yield put(acceptPDPASuccess(payload));
  } catch (error) {
    yield put(acceptPDPAFailure(handleClientError(error)));
  }
}

function* acceptDisclaimerSaga({ payload: code }) {
  try {
    yield call(acceptDisclaimerProcess, { code });
    yield put(acceptDisclaimerSuccess(code));
  } catch (error) {
    yield put(acceptDisclaimerFailure(handleClientError(error)));
  }
}

function* acceptDisclaimerProcess(payload) {
  const language = yield select(MasterSelector.locale);
  const disclaimerPayload = { language, ...payload };
  const response = yield call(DisclaimerAPI.acceptDisclaimer, disclaimerPayload);
  return response;
}

function* checkAgeAndDataIncomplete() {
  const response = yield call(DisclaimerAPI.checkAgeAndDataIncomplete);
  const { data, age, missingFields } = response.data.result;
  const isCurrThaiLang = yield select((store) => isThai(store));
  const dataIncompleteMsg = missingFields
    ? missingFields.reduce((acc, msg) => `${acc}, ${isCurrThaiLang ? msg.fieldTh : msg.fieldEn}`, "")
    : null;
  return { data, age, params: [dataIncompleteMsg] };

  // TEST DATA INCOMPLETE
  // return { data: '73004', age: null, params: ['ชื่อขาด, นามสกุลขาดหวิ่น, ชื่อเสียงหลุดร่อน'] }
}

function* checkFatcaAml() {
  const response = yield call(DisclaimerAPI.checkFatcaAml);
  let { aml, fatca, ma } = response.data.result;

  if (ma) {
    yield put(maskAsMuleAccount());
  }

  if (fatca === "NO_DATA") {
    fatca = FATCA.NO_DATA;
  }
  const valid = !(aml ?? fatca);
  const representErrorCode = aml || fatca;
  return [valid, representErrorCode];
}

function* checkAcceptPDPA() {
  const response = yield call(DisclaimerAPI.checkAcceptPDPA);
  const alreadyAccept = !!response.data?.result?.accept;
  return alreadyAccept;
}

function* investorPropertiesInvalid(payload) {
  const actions = {
    onCancel: (dispatch) => dispatch(signOut()),
  };
  yield put(replace(Routes.SignIn));
  yield put(alertError({ ...payload, actions }));
}

function* notAcceptSaga() {
  yield put(signOut());
}

function* getDisclaimerSaga({ payload }) {
  try {
    const disclaimers = yield select(DisclaimerSelector.disclaimers);

    if (disclaimers?.length > 0) {
      yield put(getDisclaimerListSuccess(disclaimers));
    } else {
      const response = yield call(DisclaimerAPI.getDisclaimerList, payload);
      const disclaimers = (response.data.result ?? []).reduce((acc, disclaimer) => {
        acc[disclaimer.code] = disclaimer.message;
        return acc;
      }, {});
      yield put(getDisclaimerListSuccess(disclaimers));
    }
  } catch (error) {
    yield put(getDisclaimerListFailure(handleClientError(error)));
  }
}

const saga = [
  takeLeading(getDisclaimerList.type, getDisclaimerSaga),
  takeLeading(accept.type, acceptAndValidateSaga),
  takeLeading(notAccept.type, notAcceptSaga),
  takeLeading(acceptDisclaimer.type, acceptDisclaimerSaga),
  takeLeading(acceptPDPA.type, acceptPDPASaga),
  takeLeading(redirectToPrimaryProduct.type, redirectToPrimaryProductSaga),
  takeLeading(redirectToSecondaryProduct.type, redirectToSecondaryProductSaga),
  takeLeading(redirectToBuySellSwitch.type, redirectToBuySellSwitchSaga),
  takeLeading(checkRegistered.type, checkRegister),
  takeLeading(redirectToRiskResult.type, redirectToRiskResultSaga),
  takeLeading(redirectToModelPortfolio.type, redirectToModelPortfolioSaga),
];

export default saga;
